import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { waLink } from "../lib/api";
import { formatPrice } from "../lib/api";
import { BadgeCheck, BedDouble, Heart, MapPin, Ruler, ChevronLeft, ChevronRight, MessageCircle, PhoneCall, ShoppingCart } from "lucide-react";
import { toast } from "sonner";

const MODE_STYLE = {
  rent: "bg-blue-600",
  buy: "bg-emerald-600",
  sale: "bg-amber-600",
};

export default function PropertyCard({ property: p, onUnsave }) {
  const [idx, setIdx] = useState(0);
  const { user, openAuth } = useAuth();
  const navigate = useNavigate();
  const images = p.images?.length ? p.images : [];

  const cycle = (dir) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => (i + dir + images.length) % images.length);
  };

  const save = async (kind) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return openAuth();
    try {
      await api.post("/saved", { property_id: p.property_id, kind });
      toast.success(kind === "cart" ? "Added to shortlist cart" : "Added to favorites");
    } catch {
      toast.error("Could not save property");
    }
  };

  return (
    <div
      data-testid={`property-card-${p.property_id}`}
      className="group overflow-hidden rounded-2xl border bg-card shadow-sm transition-shadow duration-300 hover:shadow-xl"
    >
      <Link to={`/property/${p.property_id}`} className="block">
        <div className="relative h-52 overflow-hidden">
          {images.length > 0 && (
            <img
              src={images[idx]}
              alt={p.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          )}
          <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider text-white ${MODE_STYLE[p.mode] || "bg-slate-600"}`}>
            For {p.mode}
          </span>
          {p.verified && (
            <span className="absolute right-3 top-3 flex items-center gap-1 rounded-full bg-white/90 px-2 py-1 text-xs font-semibold text-emerald-700">
              <BadgeCheck className="h-3.5 w-3.5" /> Verified
            </span>
          )}
          {images.length > 1 && (
            <>
              <button data-testid={`card-prev-${p.property_id}`} onClick={cycle(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button data-testid={`card-next-${p.property_id}`} onClick={cycle(1)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/40 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <ChevronRight className="h-4 w-4" />
              </button>
              <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1">
                {images.map((_, i) => (
                  <span key={i} className={`h-1.5 w-1.5 rounded-full ${i === idx ? "bg-white" : "bg-white/50"}`} />
                ))}
              </div>
            </>
          )}
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-heading text-base font-semibold leading-snug">{p.title}</h3>
          </div>
          <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" /> {p.locality ? `${p.locality}, ` : ""}{p.city}
            {p.pin_code && <span className="font-price text-xs">· {p.pin_code}</span>}
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {p.bhk && <span className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><BedDouble className="h-3 w-3" />{p.bhk}</span>}
            {p.area_sqft > 0 && <span className="flex items-center gap-1 rounded-md bg-secondary px-2 py-1"><Ruler className="h-3 w-3" />{p.area_sqft} sq.ft</span>}
            {p.furnishing && <span className="rounded-md bg-secondary px-2 py-1 capitalize">{p.furnishing}</span>}
          </div>
          <p className="mt-3 font-price text-xl font-bold text-emerald-700">{formatPrice(p.price, p.mode)}</p>
        </div>
      </Link>
      <div className="flex items-center gap-2 border-t px-4 py-3">
        <button data-testid={`favorite-btn-${p.property_id}`} onClick={save("favorite")} className="rounded-full p-2 text-slate-500 transition-colors hover:bg-rose-50 hover:text-rose-600" title="Favorite">
          <Heart className="h-4 w-4" />
        </button>
        <button data-testid={`cart-btn-${p.property_id}`} onClick={save("cart")} className="rounded-full p-2 text-slate-500 transition-colors hover:bg-blue-50 hover:text-blue-600" title="Add to shortlist cart">
          <ShoppingCart className="h-4 w-4" />
        </button>
        <a
          data-testid={`whatsapp-btn-${p.property_id}`}
          href={waLink(`Hi! I'm interested in "${p.title}" (${p.city}${p.pin_code ? ", " + p.pin_code : ""}) listed on Rent-ya-Buy.com`)}
          target="_blank" rel="noreferrer"
          className="rounded-full p-2 text-slate-500 transition-colors hover:bg-emerald-50 hover:text-emerald-600" title="WhatsApp enquiry"
        >
          <MessageCircle className="h-4 w-4" />
        </a>
        <button
          data-testid={`view-contact-btn-${p.property_id}`}
          onClick={() => navigate(`/property/${p.property_id}#contact`)}
          className="ml-auto flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-blue-700"
        >
          <PhoneCall className="h-3.5 w-3.5" /> Owner Contact
        </button>
      </div>
    </div>
  );
}
